import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, User, Calendar, TrendingUp, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ServerCirclePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('group');

  const groupPlans = [
    { size: '3-5 People', duration: '3 Months', contribution: '₦10,000-50,000', payout: 'Weekly/Monthly' },
    { size: '6-8 People', duration: '6 Months', contribution: '₦20,000-100,000', payout: 'Bi-weekly/Monthly' },
    { size: '9-10 People', duration: '12 Months', contribution: '₦50,000-200,000', payout: 'Monthly' }
  ];

  const benefits = [
    { icon: <Shield className="w-8 h-8" />, title: 'Secure & Transparent', desc: 'Your money is safe with bank-level security' },
    { icon: <Calendar className="w-8 h-8" />, title: 'Flexible Schedules', desc: 'Daily, weekly, or monthly contributions' },
    { icon: <Users className="w-8 h-8" />, title: 'Build Together', desc: 'Save with friends, family, or colleagues' },
    { icon: <TrendingUp className="w-8 h-8" />, title: 'Guaranteed Returns', desc: 'Get your pooled savings on schedule' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
            Server Circle Savings
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Join or create rotating savings groups (Esusu/Ajo) with friends, family, or colleagues. Save together, build wealth together.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
              <TabsTrigger value="group" className="text-lg py-3">
                <Users className="w-5 h-5 mr-2" />
                Group Savings
              </TabsTrigger>
              <TabsTrigger value="individual" className="text-lg py-3">
                <User className="w-5 h-5 mr-2" />
                Individual Savings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="group" className="space-y-16">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4">Group Savings Plans</h2>
                <p className="text-lg text-slate-600">
                  Create a circle with 3-10 people. Each member contributes equally, and takes turns receiving the pooled amount.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {groupPlans.map((plan, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-8 shadow-sm border-2 border-slate-200 hover:border-brand-blue transition-all">
                    <h3 className="text-2xl font-bold text-slate-900 mb-6">{plan.size}</h3>
                    <div className="space-y-4 mb-8">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Duration</span>
                        <span className="font-semibold">{plan.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Contribution</span>
                        <span className="font-semibold">{plan.contribution}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Payout</span>
                        <span className="font-semibold">{plan.payout}</span>
                      </div>
                    </div>
                    <Button className="w-full bg-brand-blue text-white rounded-xl py-6">
                      Create Group
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="individual" className="space-y-16">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4">Individual Savings Plans</h2>
                <p className="text-lg text-slate-600">
                  Save alone with automated contributions. Set your goal and watch your savings grow steadily.
                </p>
              </div>

              <div className="max-w-4xl mx-auto bg-white rounded-2xl p-12 shadow-lg border border-slate-200">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold mb-4">Flexible Terms</h3>
                    <ul className="space-y-3 text-slate-600">
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Daily contributions from ₦500</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Weekly savings from ₦5,000</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Monthly plans from ₦20,000</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Duration: 3, 6, or 12 months</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4">Benefits</h3>
                    <ul className="space-y-3 text-slate-600">
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Automated deductions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Track progress in real-time</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>Withdraw anytime after 30 days</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm">✓</span>
                        <span>0.5% monthly interest</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <Button className="w-full bg-brand-blue text-white rounded-xl py-6 mt-8">
                  Start Individual Savings
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-heading text-slate-900 mb-12 text-center">Why Choose Server Circle?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center mx-auto mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl p-12 text-white text-center">
          <h2 className="text-4xl font-bold font-heading mb-6">Ready to Start Saving?</h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands already building wealth through Server Circle
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-10 py-6 text-lg font-semibold"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
